import { useAtom } from 'jotai';
import { ExternalLink } from 'lucide-react';
import { selectedEntityAtom, poisAtom } from '../State';
import { SidePanelOverlay } from './SidePanelOverlay';
import { EntityFields } from './EntityFields';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18next from 'i18next';
import { Entity, PoITypeI18N, getEntityType } from '@the-real-insight/tri-model';
import BuildingProperties from '../object-properties/BuildingProperties';
import WayProperties from '../object-properties/WayProperties';
import AreaProperties from '../object-properties/AreaProperties';
import LocalGovernmentProperties from '../object-properties/LocalGovernmentProperties';
import PostalCodeProperties from '../object-properties/PostalCodeProperties';
import ParcelProperties from '../object-properties/ParcelProperties';

import { Button } from '@/components/ui/button';

export const GlobalSidebar = () => {
    const [selectedEntity, setSelectedEntity] = useAtom(selectedEntityAtom) as any;
    const [, setPois] = useAtom(poisAtom);
    const { t, i18n: _i18n } = useTranslation();
    const navigate = useNavigate();

    const onClose = () => {
        console.log('GlobalSidebar: closing panel, setting selectedEntity to null');
        setSelectedEntity(null);
    };

    const lang = i18next.resolvedLanguage || 'de';

    // Determine titles and labels
    let typeName = t('ui.object') || 'Objekt';
    if (selectedEntity) {
        try {
            typeName = selectedEntity.getTypeName ? (selectedEntity as Entity).getTypeName() : (selectedEntity.class || selectedEntity._type || typeName);
        } catch (e) {}
    }

    let typeLabel = typeName;
    if (selectedEntity) {
        try {
            if (selectedEntity.poiType === 'pollutionData') {
                typeLabel = 'Umweltdaten';
            } else if (selectedEntity.getTypeClass && selectedEntity.getTypeClass()) {
                const typeClass = selectedEntity.getTypeClass();
                typeLabel = typeClass.type?.label?.[lang] || typeClass.label?.[lang] || typeName;
            } else if (selectedEntity._type) {
                try {
                    const EntityClass = getEntityType(selectedEntity._type);
                    if (EntityClass && (EntityClass as any).type) {
                        const typeObj = (EntityClass as any).type;
                        typeLabel = typeObj.getLabel ? typeObj.getLabel(lang) : (typeObj.label?.[lang] || selectedEntity._type);
                    } else {
                        typeLabel = selectedEntity._type;
                    }
                } catch (e) {
                    typeLabel = selectedEntity._type;
                }
            } else if (selectedEntity.poiType) {
                typeLabel = (((PoITypeI18N as any)[lang] || {})[selectedEntity.poiType] || typeName);
            }
        } catch (e) {}
    }

    let title = selectedEntity?.name || t('ui.unnamed') || 'Unbenannt';
    if (selectedEntity) {
        if (selectedEntity.poiType === 'pollutionData') {
            title = 'PFAS Schadstoffdaten';
        } else if (selectedEntity.getTypeName || selectedEntity._type) {
            const tn = selectedEntity.getTypeName ? selectedEntity.getTypeName() : selectedEntity._type;
            if (tn === 'Parcel' && (selectedEntity as any).getFullName) {
                title = (selectedEntity as any).getFullName();
            } else if (tn === 'Building') {
                const b = selectedEntity as any;
                const line = `${b.street || ''} ${b.streetNumber || ''}`.trim();
                title = line || title;
            } else if (tn === 'Bridge' || tn === 'LegalPerson') {
                title = selectedEntity.name || t('bridgePropertiesPanel.unnamedBridge');
            }
        }
    }

    const renderContent = () => {
        if (!selectedEntity) return null;
        // PFAS Logic from MapAndImage
        if (selectedEntity.poiType === 'pollutionData') {
            return (
                <div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Datensatz</div>
                        <div>{selectedEntity.datasetId} - {selectedEntity.datasetName}</div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Quelle</div>
                        <div><a href={selectedEntity.source} target='_blank' rel="noreferrer">{selectedEntity.source}</a></div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Matrix</div>
                        <div>{selectedEntity.matrix}</div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Type</div>
                        <div>{selectedEntity.type}</div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Sektor</div>
                        <div>{selectedEntity.sector}</div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>Kategorie</div>
                        <div>{selectedEntity.category}</div>
                    </div>
                    <div className='flex flex-row gap-[7px] text-sm'>
                        <div className='w-[100px]'>PFAS-Summe</div>
                        {selectedEntity.pfasSum !== undefined ? <div>{selectedEntity.pfasSum} {selectedEntity.unit}</div> : <div>-</div>}
                    </div>
                    {(selectedEntity.pfasDetails || []).map((details: any, idx: number) => (
                        <div key={idx} className='flex flex-row gap-[7px] text-sm'>
                            <div className='w-[100px]'>{details.substance}</div>
                            <div className='w-[50px]'>{details.value}</div>
                            <div className='w-[50px]'>{details.unit}</div>
                        </div>
                    ))}
                </div>
            );
        }

        const entityClass = selectedEntity.class || (selectedEntity.getTypeName ? selectedEntity.getTypeName() : (selectedEntity._type || ''));
        
        // Use specialized panels for complex types
        switch (entityClass) {
            case 'Way':
                return <WayProperties way={selectedEntity}></WayProperties>;
            case 'Tile':
                return <AreaProperties area={selectedEntity}></AreaProperties>;
            case 'LocalGovernment':
                return <LocalGovernmentProperties area={selectedEntity}></LocalGovernmentProperties>;
            case 'PostalCode':
                return <PostalCodeProperties postalCode={selectedEntity}></PostalCodeProperties>;
            case 'PlotOfLand':
            case 'Parcel':
                return <ParcelProperties parcel={selectedEntity} poisChanged={setPois}></ParcelProperties>;
            case 'Building': 
                return <BuildingProperties building={selectedEntity} poisChanged={setPois}></BuildingProperties>;
        }

        // If the entity is typed, use metadata-driven field list via EntityFields
        if (typeof (selectedEntity as any).getTypeClass === 'function') {
            return (
                <div>
                    {selectedEntity._id && (
                        <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            className='mt-3 gap-2'
                            data-testid='chat-entity-details'
                            onClick={() => {
                                const typeName = selectedEntity.getTypeName ? selectedEntity.getTypeName() : selectedEntity.class;
                                navigate(`/entity?selectedEntityType=${typeName}&selectedEntity=${selectedEntity._id}`);
                                onClose();
                            }}
                        >
                            <ExternalLink className='h-4 w-4' aria-hidden />
                            Details anzeigen
                        </Button>
                    )}
                    <EntityFields
                        entity={selectedEntity}
                        hiddenFields={['poiType', 'lat', 'lon', 'osmId', 'osmType', 'country', 'shape', 'shapeType', 'coordinates', 'geometry', 'getTypeName', 'display', 'entity']}
                        valueWidth="100%"
                    />
                </div>
            );
        }

        // Default fallback for plain objects
        return (
            <div>
                {selectedEntity._id && (
                    <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='mt-3 gap-2'
                        data-testid='chat-entity-details'
                        onClick={() => {
                            const typeName = selectedEntity.getTypeName ? selectedEntity.getTypeName() : selectedEntity.class;
                            navigate(`/entity?selectedEntityType=${typeName}&selectedEntity=${selectedEntity._id}`);
                            onClose();
                        }}
                    >
                        <ExternalLink className='h-4 w-4' aria-hidden />
                        Details anzeigen
                    </Button>
                )}
                <EntityFields 
                    entity={selectedEntity} 
                    hiddenFields={['poiType', 'lat', 'lon', 'osmId', 'osmType', 'country', 'shape', 'shapeType', 'coordinates', 'geometry', 'getTypeName', 'display', 'entity']} 
                    valueWidth="100%"
                />
            </div>
        );
    };

    return (
        <SidePanelOverlay
            isOpen={!!selectedEntity}
            onClose={onClose}
            title={title}
            subtitle={typeLabel}
            width="25vw"
        >
            {renderContent()}
        </SidePanelOverlay>
    );
};
